import type { ReactElement } from 'react';
import styled from 'styled-components';
import shallow from 'zustand/shallow';
import { useCcConfig, useSettingsModal } from '../store';
import { Modal } from './modal';
import { Toggle } from './toggle';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 22px 1fr auto;
  align-items: start;
  gap: 12px;
  padding: 12px 4px;

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.07);
  }

  i {
    margin-top: 2px;
    text-align: center;
    font-size: 16px;
    color: rgba(176, 186, 209, 0.7);
  }

  .label {
    color: var(--white);
    font-size: 14px;
    font-weight: 600;
  }

  .hint {
    margin-top: 3px;
    color: rgba(176, 186, 209, 0.6);
    font-size: 12px;
    line-height: 1.45;
  }
`;

interface Setting {
  key: string;
  icon: string;
  label: string;
  hint: string;
  on: boolean;
  toggle: () => void;
}

/*
 * Display settings, mounted once in the layout. Each is a live store value, so flipping one takes effect
 * immediately — turning autoplay off unmounts the players and frees their buffers, turning the sidebar
 * feeds off unmounts them and stops their requests. localStorage is only how a choice survives a reload.
 */
export const SettingsModal = (): ReactElement => {
  const { open, setOpen } = useSettingsModal((v) => ({ open: v.open, setOpen: v.setOpen }), shallow);

  const config = useCcConfig(
    (v) => ({
      autoplayCards: v.autoplayCards,
      autoplaySidebar: v.autoplaySidebar,
      showSidebarFeeds: v.showSidebarFeeds,
      setAutoplayCards: v.setAutoplayCards,
      setAutoplaySidebar: v.setAutoplaySidebar,
      setShowSidebarFeeds: v.setShowSidebarFeeds
    }),
    shallow
  );

  const settings: Setting[] = [
    {
      key: 'autoplay-cards',
      icon: 'fal fa-play-circle',
      label: 'Autoplay on event cards',
      hint: 'Plays live previews on the events list. Turn off when many events are live at once.',
      on: config.autoplayCards,
      toggle: (): void => {
        config.setAutoplayCards(!config.autoplayCards);
      }
    },
    {
      key: 'autoplay-sidebar',
      icon: 'fal fa-columns',
      label: 'Autoplay in sidebar',
      hint: 'Plays live previews on the sidebar event cards.',
      on: config.autoplaySidebar,
      toggle: (): void => {
        config.setAutoplaySidebar(!config.autoplaySidebar);
      }
    },
    {
      key: 'sidebar-feeds',
      icon: 'fal fa-list',
      label: 'Sidebar event feeds',
      hint: 'The Live and My Events lists in the sidebar. Turning them off stops their requests.',
      on: config.showSidebarFeeds,
      toggle: (): void => {
        config.setShowSidebarFeeds(!config.showSidebarFeeds);
      }
    }
  ];

  return (
    <Modal
      open={open}
      title='Settings'
      width={460}
      onClose={(): void => {
        setOpen(false);
      }}
    >
      <List>
        {settings.map((setting) => (
          <Row key={setting.key}>
            <i className={setting.icon} />
            <div>
              <div className='label'>{setting.label}</div>
              <div className='hint'>{setting.hint}</div>
            </div>
            <Toggle on={setting.on} label={setting.label} onChange={setting.toggle} />
          </Row>
        ))}
      </List>
    </Modal>
  );
};
